// 261010 Epic 2 / P4b-2 + P8e — bước 2 "Tài sản": ba tab Nhân vật · Bối cảnh · Đạo cụ. P8e nhúng Bank cũ (Nhân vật/Đạo cụ cấp plan) + dải thiếu.
// Tab đang chọn + dữ liệu Bối cảnh sống Ở ĐÂY và KHÔNG `key` theo `eventId` ⇒ đổi video giữ tab và không nạp lại `location-needs`.
import WorkflowAssetsTasksPanel from './WorkflowAssetsTasksPanel';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Spin } from 'antd';
import { filmApi } from '../../services/api/film';
import { takeWorkflowHandoff } from '../../services/api/workflowReadHandoff';
import { AK } from './workflowAssetsPreload';
import { useAliveRef } from '../../hooks/useAliveRef';
import type { EventPlan, FilmEvent, FilmRunningJob } from '../../types/film';
import type { LocationNeedsResponse } from '../../types/filmLocationAssets';
import { apiErrorText } from './apiErrorText';
import WorkflowStyleSection from './WorkflowStyleSection'; // 261010 P3c
import { seedPlanFromEventPlan } from './filmPlanSeed';
import { FilmPlanProvider, useFilmPlan } from './FilmPlanContext';
import { WorkflowRunProvider } from './WorkflowRunContext'; // P9D #6 (I10)
import FilmReferenceTab from './FilmReferenceTab';
import FilmPropBank from './FilmPropBank';
import FilmLocationBank from './FilmLocationBank';
import FilmWorldSettingPanel from './FilmWorldSettingPanel';
import { useBankVars } from './useBankVars';
import { useBankPrep } from './useBankPrep';
import WorkflowAssetPrepStrip from './WorkflowAssetPrepStrip';
import { createCoalescer, tabDotsOf } from './assetPrepView';
import { isRunActive } from './styleProfilePolicy';
import './locationAssets.css';
import {
  DEFAULT_ASSET_TAB, assetTabLabels, assetTabReducer, characterRowsOf, createNeedsLoader, type AssetTabKey,
} from './assetsTabsUtils';

interface Props {
  planId: string;
  storyId: string;
  ev: FilmEvent | null;
  detail: EventPlan | null;
  /** Việc nền của plan (từ `workflow-status`) — hồ sơ phong cách khoá nút ghi khi máy tự chạy đang chạy. */
  jobs?: FilmRunningJob[];
  onActionDone: () => void;
  /** Dòng lỗi gọn của lượt chạy Tài sản (nếu có) — đứng ngay dưới các tab, trước thẻ hồ sơ phong cách. */
  notice?: React.ReactNode;
}

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => <p className="muted" style={{ margin: '6px 0' }}>{children}</p>;

const PlanGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { plan, loadingPlan, reloadPlan } = useFilmPlan();
  if (loadingPlan && !plan) return <div style={{ padding: 16 }}><Spin size="small" /></div>;
  if (!plan) {
    return (
      <Empty>Không tải được plan — thử lại{' '}
        <button type="button" className="wf-btn ghost" onClick={reloadPlan}>Thử lại</button>
      </Empty>
    );
  }
  return <>{children}</>;
};

function CharacterPane({ onChanged,workflowPlanId }: { onChanged: () => void;workflowPlanId?:string }) {
  const {plan}=useFilmPlan();
  const opted=!!(plan as any)?.visualBible?.workflowGeneration;
  // UI-assettabs: bỏ dòng liệt kê nhân vật theo video đang mở — thông tin theo từng video thuộc màn video, không thuộc bước Tài sản.
  return (
    <PlanGate>
      <FilmReferenceTab compact onChanged={onChanged} workflowPlanId={opted?workflowPlanId:undefined} />
    </PlanGate>
  );
}

function PropPane({ onChanged,workflowPlanId }: { onChanged: () => void;workflowPlanId?:string }) {
  const {plan}=useFilmPlan();const opted=!!(plan as any)?.visualBible?.workflowGeneration;
  return (
    <PlanGate>
      <FilmPropBank board onChanged={onChanged} workflowPlanId={opted?workflowPlanId:undefined} />
    </PlanGate>
  );
}

function LocationPane({ onChanged, onActionDone, needs, loading, error }: { onChanged: () => void; onActionDone: () => void; needs: LocationNeedsResponse | null; loading?: boolean; error?: string | null }) {
  return (
    <PlanGate>
      {error ? <p className="wf-alert WARN" role="status">{error}</p> : null}
      {loading && !needs ? <div className="muted" style={{ margin: '1em 0' }}><Spin size="small" /> Đang tải bối cảnh của plan…</div> : null}
      <FilmLocationBank variant="sky" onChanged={onChanged} onActionDone={onActionDone} needs={needs} />
    </PlanGate>
  );
}

/** BK3 — Visual Bible chỉ đọc (ghi lúc GEN_PLAN). Không editor. */
function VisualBibleRead() {
  const { plan } = useFilmPlan();
  const vb = (plan?.visualBible || {}) as Record<string, any>;
  const cast = Array.isArray(vb.cast) ? vb.cast : [];
  const elements = Array.isArray(vb.elements) ? vb.elements : [];
  if (!plan || !(vb.genre_tone || vb.palette || vb.lighting || vb.film_look || cast.length || elements.length)) return null;
  const rows: Array<[string, string]> = ([['genre_tone', 'Genre/Tone'], ['palette', 'Palette'], ['lighting', 'Lighting'], ['film_look', 'Film look']] as [string, string][])
    .filter(([k]) => vb[k]);
  return (
    <details className="wf-bible" data-testid="wf-visual-bible">
      <summary>Visual Bible · Cast ({cast.length}) · Element ({elements.length})</summary>
      {rows.map(([k, label]) => <p key={k} className="muted" style={{ margin: '4px 0' }}><b>{label}:</b> {String(vb[k])}</p>)}
      {cast.length ? <p className="muted">{cast.map((c: any) => c.name).filter(Boolean).join(' · ')}</p> : null}
    </details>
  );
}

function WorldAndBible() {
  return (
    <PlanGate>
      <FilmWorldSettingPanel />
      <VisualBibleRead />
    </PlanGate>
  );
}

const WorkflowAssetsTabs: React.FC<Props> = ({ planId, storyId, ev, detail, jobs, onActionDone, notice }) => {
  const alive = useAliveRef();
  const vars = useBankVars();
  const [tab, dispatchTab] = useReducer(assetTabReducer, DEFAULT_ASSET_TAB);
  const [needs, setNeeds] = useState<LocationNeedsResponse | null>(null);
  const [needsError, setNeedsError] = useState<string | null>(null);
  const [needsLoading, setNeedsLoading] = useState(false);
  const [prepKey, setPrepKey] = useState(0);
  const loader = useRef(createNeedsLoader((id) => filmApi.getLocationNeeds(id), (id) => takeWorkflowHandoff(AK.needs(id), () => filmApi.getLocationNeeds(id))));   // P3 F1: lần nạp đầu lấy từ chỗ giao (đọc sớm)
  const planRef = useRef(planId);
  planRef.current = planId;
  const onActionDoneRef = useRef(onActionDone);
  onActionDoneRef.current = onActionDone;
  const coalescer = useMemo(() => createCoalescer(() => { onActionDoneRef.current(); }, 700), []);
  useEffect(() => () => coalescer.flush(), [coalescer]);

  const fetchNeeds = useCallback(async (force: boolean) => {
    setNeedsLoading(true); setNeedsError(null);
    try {
      const r = await (force ? loader.current.reload(planId) : loader.current.load(planId));
      if (alive.current && planRef.current === planId) setNeeds(r);
    } catch (e) {
      if (alive.current && planRef.current === planId) setNeedsError(apiErrorText(e, 'Tải bối cảnh của plan'));
    } finally {
      if (alive.current && planRef.current === planId) setNeedsLoading(false);
    }
  }, [planId, alive]);

  // Nạp theo planId (KHÔNG theo video). Đổi plan ⇒ bỏ dữ liệu cũ + về tab mặc định.
  useEffect(() => {
    setNeeds(null);setNeedsError(null);setNeedsLoading(false); dispatchTab({ type: 'PLAN_CHANGED' });
    // Nhu cầu bối cảnh chỉ nạp khi tab Bối cảnh được mở.
  }, [fetchNeeds]);

  useEffect(()=>{if(tab==='LOCATIONS'&&!needs&&!needsLoading&&!needsError)void fetchNeeds(false);},[tab,needs,needsLoading,needsError,fetchNeeds]);

  const prep = useBankPrep(storyId, planId, prepKey);
  const onChanged = useCallback(() => {
    setPrepKey((k) => k + 1);
    coalescer.schedule();
  }, [coalescer]);

  const chars = useMemo(() => characterRowsOf(ev, detail?.refBank), [ev, detail?.refBank]);
  // 261029 P1 B2a: plan hạt giống từ chi tiết trang đã có ⇒ PlanGate mở ngay, không chờ getPlan (vẫn chạy ngầm và thay bản đầy đủ).
  const seedPlan = useMemo(() => seedPlanFromEventPlan(detail, planId, storyId), [detail, planId, storyId]);
  const labels = assetTabLabels({
    characters: chars.length, locations: needs ? needs.locations.length : null, props: 0, prep,
  });
  const dots = tabDotsOf(prep);
  const runActive = isRunActive(jobs);
  // I10: «Xem bước đang chạy» — tải lại trạng thái rồi cuộn lên danh sách việc / băng đang chạy của bước Tài sản (không retry, không lách sổ).
  const viewRunning = useCallback(() => {
    onActionDoneRef.current();
    document.querySelector('[data-testid="wf-assets-tabs"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <FilmPlanProvider key={planId} storyId={storyId} initialPlanId={planId} initialPlan={seedPlan}>
      <WorkflowRunProvider onViewRunning={viewRunning}>
      <div className="sb-scope" style={vars} data-testid="wf-assets-tabs">
        {/* UI-assets — ảnh / thẻ tài sản là thứ ĐẦU TIÊN của thân bước; phần phụ (lỗi, hồ sơ phong cách, nâng cao) xuống cuối. */}
        <WorkflowAssetPrepStrip prep={prep} onSelectTab={(t) => dispatchTab({ type: 'SELECT', tab: t })} />
        <div className="wf-tabs" role="tablist" aria-label="Tài sản">
          {labels.map((t) => (
            <button key={t.key} type="button" role="tab" id={`wf-tab-${t.key}`} aria-selected={tab === t.key} aria-controls={`wf-tabpanel-${t.key}`}
              className={`wf-tab${tab === t.key ? ' on' : ''}`} onClick={() => dispatchTab({ type: 'SELECT', tab: t.key as AssetTabKey })}>
              {dots[t.key] ? <span className={`wf-tabdot ${dots[t.key]}`} aria-hidden="true" /> : null}
              {t.name}{t.count !== null ? <span className="n">{t.count}</span> : null}
            </button>))}
        </div>
        <div role="tabpanel" id={`wf-tabpanel-${tab}`} aria-labelledby={`wf-tab-${tab}`}>
          {tab === 'CHARACTERS' && <CharacterPane workflowPlanId={planId} onChanged={onChanged} />}
          {tab === 'LOCATIONS' && (
            <LocationPane onChanged={() => { onChanged(); void fetchNeeds(true); }} onActionDone={onActionDone} needs={needs} loading={needsLoading} error={needsError} />)}
          {tab === 'PROPS' && <PropPane workflowPlanId={planId} onChanged={onChanged} />}
        </div>
        {notice}
        {runActive ? (
          <p className="wf-note">Máy tự chạy chặng đang chạy trên plan này — một số thao tác ghi sẽ bị từ chối cho tới khi xong.</p>
        ) : null}
        <WorkflowAssetsTasksPanel compact planId={planId} refreshKey={String(prepKey)} onActionDone={onActionDone} />
        {/* 261010 P3c — hồ sơ phong cách cấp TRUYỆN: một thẻ gọn, thu mặc định; cùng KHÔNG `key` theo video (đổi video không nạp lại). */}
        <WorkflowStyleSection planId={planId} jobs={jobs} onActionDone={onActionDone} />
        <details className="wf-adv" data-testid="wf-advanced">
          <summary>Nâng cao</summary>
          <WorldAndBible />
        </details>
      </div>
      </WorkflowRunProvider>
    </FilmPlanProvider>
  );
};

export default WorkflowAssetsTabs;
